import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import {
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  Gateway,
  GatewayCommands,
  GatewayConnectionMode,
  GatewayConnectionType,
  GatewayProperties,
  SignalProperties,
  UserRoleType,
} from '@vi-iot/client';

import { UIService } from '@services';
import {
  AuthStore,
  FeatureService,
  GatewayStore,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  RemoteConfigStore,
} from '@store';

import { map, withLatestFrom } from 'rxjs/operators';
import { NO_VALUE } from '../../../../app/shared/pipes/dash/dash.pipe';
import { CustomTranslationsService } from '../../../operational-data-common/translations';
import { wifiSignalThreshold } from '../../../shared/common';
import { FeatureTileIconVariant } from '../../../shared/components/feature-tile/feature-tile-variants';
import { DBM_RANGES } from '../../../shared/models/dbmRange.model';
import { SUFFIX } from '../../../shared/common/units';
import { FeaturesDetailsOverlayTypeEnum } from '../installation-overlay.model';

@Component({
  selector: 'app-gateway-features-panel',
  templateUrl: './gateway-features-panel.component.html',
  styleUrls: ['./gateway-features-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GatewayFeaturesPanelComponent implements OnInit {
  readonly featurePropertyKey = FeaturePropertyKey;
  @Input() gatewaySerial: string;
  @Input() installationId: number;
  @Input() isLegacyDevice: boolean;

  readonly wifiSignalThreshold = wifiSignalThreshold;
  readonly iconVariant = FeatureTileIconVariant;

  availableOverlays = FeaturesDetailsOverlayTypeEnum;
  wifi$: Observable<Feature<SignalProperties, never>>;
  status$: Observable<Feature<GatewayProperties, GatewayCommands>>;
  gatewayProperties$: Observable<Gateway>;
  isFirmwareOutdated$: Observable<boolean>;
  gatewayType$: Observable<string>;
  rsaRolesForDeregister = [
    UserRoleType.admin,
    UserRoleType.supportInternational,
    UserRoleType.supportInternationalWithRemoteDiagnosis,
    UserRoleType.supportLocalReadWrite,
    UserRoleType.supportLocalReadWriteWithRemoteDiagnosis,
    UserRoleType.supportLocalDepartmentManagement,
  ];
  SUFFIX = SUFFIX;
  NO_VALUE = NO_VALUE;
  GatewayConnectionMode = GatewayConnectionMode;
  featureConnectionMode$: Observable<Feature<GatewayConnectionType, never>>;
  constructor(
    public authStore: AuthStore,
    public gatewayStore: GatewayStore,
    public installationDetailsStore: InstallationDetailsStore,
    public translateService: TranslateService,
    public installationFeaturesStore: InstallationFeaturesStore,
    private featureService: FeatureService,
    private remoteConfigStore: RemoteConfigStore,
    private uiService: UIService,
    private customTranslationsService: CustomTranslationsService
  ) {}

  ngOnInit(): void {
    this.wifi$ = this.installationFeaturesStore.selectInstallationTCUFeature(
      FeatureNames.tcuWifi,
      this.gatewaySerial
    );
    this.featureConnectionMode$ = this.installationFeaturesStore.selectInstallationTCUFeature(
      FeatureNames.tcuMode,
      this.gatewaySerial
    );

    this.status$ = this.installationFeaturesStore.selectInstallationGatewayFeature(
      FeatureNames.gatewayStatus
    );
    this.gatewayProperties$ = this.featureService.selectGatewayProperties();
    this.isFirmwareOutdated$ = this.installationDetailsStore.isFirmwareUpToDate$.pipe(
      withLatestFrom(this.remoteConfigStore.azureCertificateFirmwareCheck$),
      map(
        ([isFirmwareUpToDate, azureCertificateFirmwareCheck]) =>
          !isFirmwareUpToDate && azureCertificateFirmwareCheck
      )
    );
    this.gatewayType$ = this.gatewayStore.getGatewayType();
  }

  openUpdateTile(): void {
    this.uiService.openActiveOverlay(FeaturesDetailsOverlayTypeEnum.Gateway);
  }

  dbmRangeTooltipText(): string {
    let tooltipText = '';
    const translatedPrepositionTo = this.translateService.instant(
      '+INSTALLATIONS.INSTALLATION.EVENT_LOG.SHARED.INVITATION_TO'
    );

    DBM_RANGES.forEach((range, key) => {
      tooltipText +=
        key === 0
          ? `> ${range.to} dBm: ${this.translateService.instant(
              this.customTranslationsService.WIFI_STRENGTH[range.strength]
            )}\n`
          : key === DBM_RANGES.length - 1
          ? `< ${range.from} dBm: ${this.translateService.instant(
              this.customTranslationsService.WIFI_STRENGTH[range.strength]
            )}`
          : `${range.from} dBm ${translatedPrepositionTo.toLowerCase()} ${
              range.to
            } dBm: ${this.translateService.instant(
              this.customTranslationsService.WIFI_STRENGTH[range.strength]
            )}\n`;
    });

    return tooltipText;
  }
}
